import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Typography } from '../../../../core/components/molecules/Typography';
import {
  IconDone,
  IconFav,
} from '../../../../core/components/organism/ContentCard/variants';
import { Tag } from '../../../../core/components/atoms/Tag';
import { Colors } from '../../../../core/models/disciplines';
import { IResponseFavorite, useContentFavorite } from '../../hooks/useContentFavorite';

interface ILessonCard {
  lesson: any;
  slug: string;
  discipline: string;
  module: string;
  subject: string;
  subjectSlug: string;
  done: boolean;
  favorite: boolean;
  onDone?: (id: string, slug: string) => void; 
  onClose: () => void;
  callback: (data: IResponseFavorite) => void;
}

const formatDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  if (minutes > 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hoursString = hours.toString().padStart(2, '0');
    const minutesString = remainingMinutes.toString().padStart(2, '0');
    const secondsString = seconds.toString().padStart(2, '0');
    return `${hoursString}:${minutesString}:${secondsString}`;
  }
  const minutesString = minutes.toString().padStart(2, '0');
  const secondsString = seconds.toString().padStart(2, '0');
  return `${minutesString}:${secondsString}`;
};

function LessonCard({
  lesson,
  discipline,
  module,
  subject,
  subjectSlug,
  slug,
  done,
  favorite,
  onDone,
  onClose,
  callback
}: ILessonCard) {
  const navigate = useNavigate();
  const { mutate, isLoading } = useContentFavorite(true, callback);

  const selected = lesson.slug === slug;
  const selectedBorder = selected
    ? 'border-hairline border-brand-color-primary-pure'
    : null;

  const replacementLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subjectSlug}/videoaula/${lesson.slug}`;

  const onFavorite = () => {
    mutate({
      category: 'lesson',
      targetId: lesson._id,
      targetType: 'Lesson',
    })
  }

  return (
    <div
      key={lesson._id}
      className={'flex cursor-pointer rounded-md bg-color-lightest p-4 shadow-level-1 '+selectedBorder}
    >
      <div 
        className='flex flex-1 flex-col gap-sm' 
        onClick={() => {
          if (!selected) {
            onClose();
            navigate(replacementLink);
          }
        }}
      >
        <div className='flex flex-row justify-between items-center'>
          <div className='flex items-center gap-sm'>
            <Icon
              Icon='play_circle'
              type='material'
              className='text-md text-brand-color-secondary-pure'
            />
            <Typography size='h6' weight='bold' type='titleSection'>
              VIDEOAULA
            </Typography>
          </div>
          {subject && (
            <Tag
              variant={discipline as Colors}
              className='flex flex-row items-center gap-sm px-2 mr-3'
            >
              <Typography size='small' className='text-[#fff]'>
                {subject}
              </Typography>
            </Tag>
          )}
        </div>
        <Typography type='title' weight='bold'>
          {lesson.title}
        </Typography>
        <Typography size='small'>
          {formatDuration(lesson.duration) || '--:--'}{' '}
          {lesson.teacher?.length ? `Prof. ${lesson.teacher[0]?.name}` : ''}
        </Typography>
      </div>
      <div className='flex w-fit flex-col items-end justify-between'>
        <Button 
          variant='text' 
          size='regular' 
          className='px-0 py-0'
          loading={isLoading}
          onClick={() => onDone && onDone(lesson._id, lesson.slug)}
        >
          <Icon
            Icon='check_circle'
            type='material'
            className={IconDone({ type: done ? 'done' : 'unseen' })}
          />
        </Button>
        <Button 
          onlyIcon 
          variant='text' 
          size='regular' 
          className='px-0 py-0'
          loading={isLoading}
          onClick={() => onFavorite()}
        >
          <Icon
            Icon='star'
            type='material'
            variant='rounded'
            className={IconFav({ type: favorite ? 'favorite' : 'notFavorite' })}
          />
        </Button>
      </div>
    </div>
  );
}

export default LessonCard;
